import { AppType } from "client/Api/Apps/common";
import { ReactElement, useCallback } from "react";
import { useRenderManager } from "../../useRenderManager";
import { GoogleLiveTileContent } from "./GoogleLiveTileContent";
import { OneByOneTileContent } from "../OneByOneTileContent";
import { TwoByTwoTileContent } from "../TwoByTwoTileContent";
import { navigateToGoogleApp } from "client_legacy/Apps/appNavigation";
import { useRegisterTileUsage } from "../useRegisterTileUsage";
import { TileRenderState } from "../../RenderState/common";
import { useBaseNavigation } from "../useInvokeNavigation";
import { TileContextProvider } from "../../TileContextProvider";
import { Coordinate } from "client/Misc/commonTypes";

export type GoogleTileContentProps = {
	renderState: TileRenderState<AppType.Google>;
	onEdit: (() => void) | undefined;
	onOpenTileMenu: (coordinate: Coordinate) => void;
};

export const GoogleTileContent = (props: GoogleTileContentProps): ReactElement => {
	const {
		renderState,
		onEdit,
		onOpenTileMenu
	} = props;

	const [navigationElement, navigateHandler] = useGoogleTileNavigation(renderState, onEdit);

	const {
		dimension,
		data: { settings }
	} = renderState;

	const tileContent: ReactElement = useRenderManager(
		dimension,
		() => (
			<GoogleLiveTileContent
				destinationWindow={settings.destinationWindow}
				onOpenTileMenu={onOpenTileMenu}
			/>
		),
		[
			{ columnSpan: 1, rowSpan: 1 },
			() => (
				<OneByOneTileContent
					onClick={navigateHandler}
					onOpenTileMenu={onOpenTileMenu}
				/>
			)
		],
		[
			{ columnSpan: 2, rowSpan: 2 },
			() => (
				<TwoByTwoTileContent
					onClick={navigateHandler}
					onOpenTileMenu={onOpenTileMenu}
				/>
			)
		]
	);

	return (
		<>
			<TileContextProvider
				renderState={renderState}
			>
				{tileContent}
			</TileContextProvider>

			{navigationElement}
		</>
	);
};
GoogleTileContent.displayName = "GoogleTileContent";

export function useGoogleTileNavigation(
	renderStateIsh: Pick<TileRenderState<AppType.Google>, "data" | "dimension" | "interactionState">,
	onEdit: (() => void) | undefined
): [element: ReactElement | null, () => void | Promise<void>] {
	const registerTileUsage: () => void = useRegisterTileUsage(renderStateIsh);

	const {
		settings: { destinationWindow }
	} = renderStateIsh.data;

	return useBaseNavigation(
		renderStateIsh,
		onEdit,
		useCallback(
			(): void => {
				registerTileUsage();
				navigateToGoogleApp({
					destinationWindow
				});
			},
			[destinationWindow, registerTileUsage]
		)
	);
}
